import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";
import NumberLineInterval from "../../components/visuals/NumberLineInterval";

export default function LinearInequalitiesPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        <strong>Równanie</strong> to zapis, w którym dwa wyrażenia mają być sobie równe.
        Jego rozwiązaniem jest taka liczba, która po podstawieniu w miejsce{" "}
        <InlineMath math="x" />{" "}sprawia, że lewa i prawa strona mają tę samą wartość.
      </p>
      <p>
        Równanie rozwiązujemy krok po kroku: rozwijamy nawiasy, redukujemy wyrazy podobne,
        przenosimy wyrazy z <InlineMath math="x" />{" "}na jedną stronę, a liczby na drugą.
        Po obu stronach zawsze wykonujemy to samo działanie.
      </p>
      <BlockMath math={String.raw`\begin{aligned}
3(x-2)+4&=2x+7\\
3x-2&=2x+7\\
x&=9
\end{aligned}`} />
      <p>
        Możemy sprawdzić wynik przez podstawienie <InlineMath math="x=9" />:
      </p>
      <BlockMath math={String.raw`3(9-2)+4=25\qquad\text{i}\qquad 2\cdot 9+7=25`} />
      <p>
        Obie strony są równe <InlineMath math="25" />, więc <InlineMath math="x=9" />{" "}rzeczywiście
        jest rozwiązaniem równania.
      </p>
      <p>
        <strong>Nierówność</strong> porównuje dwie wartości za pomocą znaków{" "}
        <InlineMath math={String.raw`<,\;>,\;\le,\;\ge`} />. Jej rozwiązaniem jest każda liczba, która po
        podstawieniu daje prawdziwe porównanie. Dlatego rozwiązaniem nierówności najczęściej
        nie jest jedna liczba, lecz cały przedział liczb.
      </p>
      <p>
        Nierówność przekształcamy podobnie jak równanie. Jest jedna kluczowa różnica:
        gdy mnożymy lub dzielimy obie strony przez liczbę ujemną, <strong>musimy odwrócić znak</strong>.
      </p>
      <BlockMath math={String.raw`\begin{aligned}
-3x&<12 &&\big|\;:(-3)\\
x&>-4
\end{aligned}`} />
      <p>
        Otrzymaliśmy wszystkie liczby większe od <InlineMath math="-4" />, czyli{" "}
        <InlineMath math={String.raw`x\in(-4,+\infty)`} />.
      </p>
      <p>
        Jeżeli w równaniu albo nierówności występują <strong>stałe mianowniki</strong>, najpierw:
      </p>
      <ol style={{ paddingLeft: "1.4rem", marginTop: 0 }}>
        <li>znajdujemy NWW mianowników,</li>
        <li>mnożymy przez NWW każdy składnik po obu stronach,</li>
        <li>skracamy mianowniki i rozwiązujemy otrzymane działanie bez ułamków.</li>
      </ol>
      <p>
        NWW jest liczbą dodatnią, więc samo usuwanie mianowników nie zmienia znaku nierówności.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Równanie z niewiadomą po obu stronach",
      description: "Zbierz wyrazy z x po jednej stronie.",
      content: (
        <div className="space-y-3">
          <BlockMath math={String.raw`\begin{aligned}
4x-5&=2x+9 &&\big|\;-2x\\
2x-5&=9 &&\big|\;+5\\
2x&=14 &&\big|\;:2\\
x&=7
\end{aligned}`} />
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Równanie z ułamkami: usuwamy mianowniki",
      description: "Pomnóż obie strony przez NWW mianowników.",
      content: (
        <div className="space-y-3">
          <p><InlineMath math={String.raw`\operatorname{NWW}(3,6)=6`} />, więc mnożymy całe równanie przez 6.</p>
          <BlockMath math={String.raw`\begin{aligned}
\frac{x+2}{3}&=\frac{x+8}{6}\\
6\cdot\frac{x+2}{3}&=6\cdot\frac{x+8}{6}\\
2(x+2)&=x+8\\
x&=4
\end{aligned}`} />
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Nierówność i odwrócenie znaku",
      description: "Dzielenie przez liczbę ujemną zmienia kierunek nierówności.",
      content: (
        <div className="space-y-3">
          <BlockMath math={String.raw`\begin{aligned}
-4x+3&\le 15 &&\big|\;-3\\
-4x&\le 12 &&\big|\;:(-4)\\
x&\ge -3
\end{aligned}`} />
          <div style={{ maxWidth: 620, margin: "0 auto" }}>
            <NumberLineInterval t={-3} tLabelLatex="-3" dir="right" closed showZero />
          </div>
          <p><strong>Odpowiedź:</strong> <InlineMath math={String.raw`x\in[-3,+\infty)`} />.</p>
        </div>
      ),
    },
    {
      label: "Przykład 4",
      summary: "Nierówność z ułamkami",
      description: "Najpierw NWW, potem zwykła nierówność liniowa.",
      content: (
        <div className="space-y-3">
          <p><InlineMath math={String.raw`\operatorname{NWW}(2,3)=6`} />. Mnożymy obie strony przez dodatnie 6.</p>
          <BlockMath math={String.raw`\begin{aligned}
\frac{x-1}{2}&>\frac{2x+1}{3}\\
3(x-1)&>2(2x+1)\\
3x-3&>4x+2\\
-x&>5 &&\big|\;:(-1)\\
x&<-5
\end{aligned}`} />
          <p>
            Znak odwracamy dopiero w ostatnim wierszu — przy dzieleniu przez <InlineMath math="-1" />.
          </p>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
